import { useEffect, useMemo, useState } from "react";
import {
  api, ApiError, type AdLevel, type Dashboard as DashboardRow, type DashboardSpec,
  type GeneratedDashboard, type MetricKey, type ProductRow, type SeriesKey, type Widget,
} from "../api";
import { useAsync } from "../useAsync";
import { WidgetBoard, OVERVIEW_SPEC, type QueuePlumbing } from "../components/Widgets";
import { GrowthActionsBar } from "../components/GrowthActionsBar";
import { shortTime } from "../format";

/**
 * Dashboards: the built-in Overview plus any number of saved ones. Users build
 * by hand (edit mode), describe what they want (the composer drafts it), or
 * point their agent at create_dashboard — all three land in the same spec.
 */
export function Dashboard({ product, queue }: { product: ProductRow; queue: QueuePlumbing }) {
  const slug = product.slug;
  const list = useAsync(() => api.dashboards.list(slug), [slug], { key: `dashboards:${slug}` });

  const [activeId, setActiveId] = useState<string>("overview");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DashboardSpec | null>(null);
  const [draftName, setDraftName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [creating, setCreating] = useState(false);
  // A just-created dashboard is merged in until the list refetch includes it,
  // so switching to it is race-free (no flash back to Overview).
  const [justCreated, setJustCreated] = useState<DashboardRow | null>(null);

  // Product switch: back to the built-in tab, drop any edit in progress.
  useEffect(() => {
    setActiveId("overview");
    setEditing(false);
    setDraft(null);
    setError(null);
    setJustCreated(null);
  }, [slug]);

  const saved = useMemo(() => {
    const arr = list.data ?? [];
    return justCreated && !arr.some((d) => d.id === justCreated.id) ? [...arr, justCreated] : arr;
  }, [list.data, justCreated]);
  // Once the refetched list carries it, the local copy has done its job.
  useEffect(() => {
    if (justCreated && list.data?.some((d) => d.id === justCreated.id)) setJustCreated(null);
  }, [list.data, justCreated]);
  const active = useMemo(
    () => (activeId === "overview" ? null : saved.find((d) => d.id === activeId) ?? null),
    [activeId, saved]
  );
  // A stale id (deleted elsewhere) simply renders as Overview; the tab strip
  // highlights whatever is actually on screen.
  const showingOverview = !active;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const customiseOverview = () =>
    run(async () => {
      const d = await api.dashboards.create(slug, {
        name: "My overview",
        description: "Started from the built-in Overview.",
        spec: OVERVIEW_SPEC,
      });
      setJustCreated(d);
      list.reload();
      setActiveId(d.id);
      setDraft(structuredClone(d.spec));
      setDraftName(d.name);
      setEditing(true);
    });

  const startEdit = (d: DashboardRow) => {
    setDraft(structuredClone(d.spec));
    setDraftName(d.name);
    setEditing(true);
    setConfirmingDelete(false);
    setError(null);
  };

  const saveEdit = () =>
    run(async () => {
      if (!active || !draft) return;
      await api.dashboards.update(active.id, { name: draftName.trim() || active.name, spec: draft });
      setEditing(false);
      setDraft(null);
      list.reload();
    });

  const deleteActive = () =>
    run(async () => {
      if (!active) return;
      await api.dashboards.remove(active.id);
      setJustCreated((j) => (j?.id === active.id ? null : j));
      setEditing(false);
      setDraft(null);
      setActiveId("overview");
      list.reload();
    });

  const addWidget = (w: Widget) => setDraft((d) => (d ? { widgets: [...d.widgets, w] } : d));

  const spec = editing && draft ? draft : active?.spec ?? OVERVIEW_SPEC;

  return (
    <div className="page" style={{ paddingTop: 26 }}>
      {/* ------------------------------------------------ dashboard switcher */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <button type="button" className="seg" aria-pressed={showingOverview}
                onClick={() => { setActiveId("overview"); setEditing(false); }}>
          Overview
        </button>
        {saved.map((d) => (
          <button key={d.id} type="button" className="seg" aria-pressed={activeId === d.id}
                  onClick={() => { setActiveId(d.id); setEditing(false); setError(null); }}>
            {d.name}
          </button>
        ))}
        <button type="button" className="seg" style={{ color: "var(--keeper)", borderStyle: "dashed" }}
                onClick={() => setCreating(true)}>
          + New dashboard
        </button>

        <span style={{ flex: 1 }} />

        {!editing && showingOverview && (
          <button type="button" className="btn-ghost" onClick={customiseOverview} disabled={busy}>
            Customise
          </button>
        )}
        {!editing && active && (
          <button type="button" className="btn-ghost" onClick={() => startEdit(active)}>
            Edit
          </button>
        )}
      </div>

      {/* ------------------------------------------------------- edit toolbar */}
      {editing && active && draft && (
        <div className="editbar">
          <input className="input" style={{ maxWidth: 240, minHeight: 38 }} value={draftName}
                 onChange={(e) => setDraftName(e.target.value)} aria-label="Dashboard name" />
          <AddWidgetPicker onAdd={addWidget} />
          <span style={{ flex: 1 }} />
          <button type="button" className="btn-text" style={{ color: confirmingDelete ? "var(--neg)" : undefined }}
                  onClick={() => (confirmingDelete ? deleteActive() : setConfirmingDelete(true))} disabled={busy}>
            {confirmingDelete ? "Really delete?" : "Delete"}
          </button>
          <button type="button" className="btn-ghost" disabled={busy}
                  onClick={() => { setEditing(false); setDraft(null); setConfirmingDelete(false); }}>
            Cancel
          </button>
          <button type="button" className="btn-accent" style={{ minHeight: 40 }} onClick={saveEdit} disabled={busy}>
            Save
          </button>
        </div>
      )}

      {!editing && active && (
        <div style={{ fontSize: 11.5, color: "var(--text-faint)", padding: "10px 2px 0" }}>
          {active.description ? `${active.description} · ` : ""}
          created by {active.created_by}
          {active.updated_by ? ` · updated by ${active.updated_by} ${shortTime(active.updated_at)}` : ""}
        </div>
      )}
      {!editing && showingOverview && (
        <div style={{ fontSize: 11.5, color: "var(--text-faint)", padding: "10px 2px 0" }}>
          Built-in. Customise makes your own editable copy — or describe a new one and the Keeper drafts it.
        </div>
      )}

      {error && <div className="form-error" style={{ padding: "10px 2px 0" }}>{error}</div>}

      {!editing && showingOverview && (
        <div style={{ marginTop: 18 }}>
          <GrowthActionsBar product={product} />
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <WidgetBoard spec={spec} product={product} queue={queue} editing={editing}
                     onChange={(s) => setDraft(s)} />
      </div>

      {creating && (
        <NewDashboardModal
          slug={slug}
          onClose={() => setCreating(false)}
          onCreated={(d) => {
            setCreating(false);
            setJustCreated(d);
            list.reload();
            setActiveId(d.id);
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------ add-widget

const METRIC_OPTIONS: Array<[MetricKey, string]> = [
  ["spend_30d", "Spend 30d"], ["revenue_30d", "Revenue 30d"], ["customers_30d", "New customers"],
  ["cac", "CAC"], ["ltv", "LTV"], ["ltv_cac", "LTV:CAC"], ["visits_30d", "Visits 30d"],
  ["leads_30d", "Leads 30d"], ["pending_cards", "Pending cards"],
  ["social_followers", "Social followers"], ["social_reach", "Social reach"],
  ["social_engagement_rate", "Social engagement"],
];
const SERIES_OPTIONS: Array<[SeriesKey, string]> = [
  ["revenue", "Revenue trend"], ["spend", "Spend trend"], ["visits", "Visits trend"],
  ["leads", "Leads trend"], ["customers", "Customers trend"],
  ["social_followers", "Followers trend"], ["social_impressions", "Social impressions"],
  ["social_engagement", "Social engagement"],
];

function AddWidgetPicker({ onAdd }: { onAdd: (w: Widget) => void }) {
  const id = () => `w${crypto.randomUUID().slice(0, 8)}`;
  const build = (v: string): Widget | null => {
    const [kind, key] = v.split(":");
    if (kind === "metric") return { id: id(), type: "metric", metric: key as MetricKey };
    if (kind === "series") return { id: id(), type: "timeseries", series: key as SeriesKey, days: 30 };
    if (kind === "funnel") return { id: id(), type: "funnel", days: 30 };
    if (kind === "payback") return { id: id(), type: "payback" };
    if (kind === "queue") return { id: id(), type: "queue" };
    if (kind === "breakdown") return { id: id(), type: "breakdown", measure: key as "spend" | "revenue_first" | "revenue_last", days: 30 };
    if (kind === "ads") {
      const [level, measure] = key.split("-");
      return { id: id(), type: "ad_breakdown", level: level as AdLevel,
               measure: measure as "roas" | "spend" | "revenue_first", days: 30 };
    }
    if (kind === "social_posts") return { id: id(), type: "top_social_posts", days: 30 };
    if (kind === "note") return { id: id(), type: "note", text: "Double-purpose note: context for you, prompt for your agent." };
    return null;
  };
  return (
    <select
      className="input"
      style={{ maxWidth: 190, minHeight: 38, fontSize: 13 }}
      value=""
      aria-label="Add widget"
      onChange={(e) => {
        const w = build(e.target.value);
        if (w) onAdd(w);
        e.target.value = "";
      }}
    >
      <option value="" disabled>Add widget…</option>
      <optgroup label="Metrics">
        {METRIC_OPTIONS.map(([k, label]) => <option key={k} value={`metric:${k}`}>{label}</option>)}
      </optgroup>
      <optgroup label="Trends">
        {SERIES_OPTIONS.map(([k, label]) => <option key={k} value={`series:${k}`}>{label}</option>)}
      </optgroup>
      <optgroup label="Panels">
        <option value="funnel:">Funnel</option>
        <option value="payback:">Payback curve</option>
        <option value="queue:">Action queue</option>
        <option value="breakdown:revenue_first">Revenue by channel</option>
        <option value="breakdown:spend">Spend by channel</option>
        <option value="social_posts:">Top social posts</option>
        <option value="note:">Note</option>
      </optgroup>
      <optgroup label="Ads">
        <option value="ads:campaign-roas">ROAS by campaign</option>
        <option value="ads:keyword-roas">ROAS by keyword</option>
        <option value="ads:ad-roas">ROAS by creative</option>
        <option value="ads:campaign-spend">Spend by campaign</option>
        <option value="ads:keyword-spend">Spend by keyword</option>
        <option value="ads:keyword-revenue_first">Revenue by keyword</option>
      </optgroup>
    </select>
  );
}

// -------------------------------------------------------- new-dashboard

function NewDashboardModal({
  slug, onClose, onCreated,
}: {
  slug: string;
  onClose: () => void;
  onCreated: (d: DashboardRow) => void;
}) {
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [draft, setDraftPreview] = useState<GeneratedDashboard | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const preview = () =>
    run(async () => {
      const g = await api.dashboards.generate(slug, prompt.trim());
      setDraftPreview(g);
      if (!name.trim()) setName(g.name);
    });

  const create = (spec?: DashboardSpec) =>
    run(async () => {
      const d = await api.dashboards.create(slug, {
        name: name.trim() || draft?.name || "Custom dashboard",
        spec: spec ?? draft?.spec,
        prompt: !spec && !draft && prompt.trim() ? prompt.trim() : undefined,
      });
      onCreated(d);
    });

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="New dashboard" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>New dashboard</h2>

        <div className="field">
          <label htmlFor="nd-name">Name</label>
          <input id="nd-name" className="input" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. Ads deep-dive" maxLength={80} />
        </div>

        <div className="field">
          <label htmlFor="nd-prompt">Describe it and the Keeper drafts it (optional)</label>
          <textarea id="nd-prompt" className="input" style={{ minHeight: 74, padding: "10px 12px", resize: "vertical" }}
                    value={prompt}
                    onChange={(e) => { setPrompt(e.target.value); setDraftPreview(null); }}
                    placeholder="“revenue and spend trends, the funnel, and where customers come from”"
                    maxLength={500} />
          <span className="form-note">
            Deterministic composer — it says exactly what it matched. Agents can build richer ones via the{" "}
            <code>create_dashboard</code> MCP tool.
          </span>
        </div>

        {prompt.trim().length >= 3 && !draft && (
          <button type="button" className="btn-solid" onClick={preview} disabled={busy}>
            Draft it
          </button>
        )}

        {draft && (
          <div className="banner" style={{ flexDirection: "column", gap: 6, alignItems: "stretch" }}>
            <span>
              {draft.matched.length > 0
                ? <>Matched: <strong>{draft.matched.join(", ")}</strong>.</>
                : <>Nothing specific matched — drafting a compact overview instead.</>}
            </span>
            <span style={{ color: "var(--text-4)" }}>
              {draft.spec.widgets.length} widgets. You can rearrange everything after saving.
            </span>
          </div>
        )}

        {error && <div className="form-error">{error}</div>}

        <div style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 4 }}>
          <button type="button" className="btn-text" onClick={onClose} disabled={busy}>Cancel</button>
          <span style={{ flex: 1 }} />
          {!draft && (
            <button type="button" className="btn-ghost" onClick={() => create(OVERVIEW_SPEC)} disabled={busy}>
              Copy Overview
            </button>
          )}
          <button type="button" className="btn-accent" style={{ minHeight: 40 }} disabled={busy}
                  onClick={() => create(draft ? draft.spec : prompt.trim() ? undefined : { widgets: [] })}>
            {draft ? "Create from draft" : prompt.trim() ? "Create from description" : "Create blank"}
          </button>
        </div>
      </div>
    </div>
  );
}
